import { usePeriodeStore } from "@/hooks/use-periode";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useSearchParams } from "react-router";
import { Loader2, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Cookies from "js-cookie";
import { useRef } from "react";
const formatRupiah = (angka: string | number | null | undefined) => {
  if (angka === null || angka === undefined) return "-";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(angka));
};

export default function BesaranSerdosPage() {
  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester, periodeId } = getYearAndSemester();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const activeToken = Cookies.get("auth_token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${baseUrl}/api/v1/master-periode-besaran-serdos/${periodeId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${activeToken}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to upload file");
      }

      return response.json();
    },
    onSuccess: () => {
      alert("File berhasil diunggah!");
      queryClient.invalidateQueries({ queryKey: ["besaran-serdos"] });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    onError: (err: any) => {
      alert(`Gagal mengunggah file: ${err.message}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      importMutation.mutate(e.target.files[0]);
    }
  };

  const pageParam = searchParams.get("page");
  const limitParam = searchParams.get("limit");

  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const limit = limitParam ? parseInt(limitParam, 10) : 10;

  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const setPage = (newPage: number) => {
    updateParams({ page: newPage.toString() });
  };

  const setLimit = (newLimit: number) => {
    updateParams({ limit: newLimit.toString(), page: "1" });
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["besaran-serdos", periodeId, page, limit],
    queryFn: async () => {
      if (!periodeId) return { datas: [], pagination: null };
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      return apiClient<any>(
        `${baseUrl}/api/v1/master-periode-besaran-serdos/${periodeId}?${params.toString()}`,
      );
    },
    enabled: !!periodeId,
  });

  const listData = data?.datas || [];
  const totalData = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || undefined;
  const hasMore = totalPages ? page < totalPages : listData.length === limit;

  // Generate column names based on semester
  const monthNamesGenap = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
  ];
  const monthNamesGanjil = [
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];

  const columns = semester === 2 ? monthNamesGenap : monthNamesGanjil;

  return (
    <div className="container mx-auto py-8 space-y-6 px-4 md:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-800">
            Besaran Serdos
          </h1>
          <p className="text-slate-500 mt-2">
            Manajemen besaran serdos dosen per bulan pada periode aktif.
          </p>
        </div>
        <Badge
          variant="secondary"
          className="px-4 py-1.5 text-sm w-fit shrink-0 bg-slate-100 text-slate-700 shadow-sm"
        >
          Periode: {year} / Semester {semester === 1 ? "Ganjil" : "Genap"}
        </Badge>
      </div>

      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle className="text-lg text-slate-700">
              Data Besaran Serdos
            </CardTitle>
            {isLoading && (
              <div className="flex items-center text-sm text-primary">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Memuat data...
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg shadow-sm border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:text-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending || !periodeId}
            >
              {importMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {importMutation.isPending ? "Mengunggah..." : "Import Excel"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {isError ? (
            <div className="flex h-40 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600">
              Terjadi kesalahan saat memuat data.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <Table>
                  <TableHeader className="bg-slate-50 border-b border-slate-200">
                    <TableRow className="hover:bg-transparent border-none">
                      <TableHead className="w-16 text-center font-bold text-slate-600">
                        No
                      </TableHead>
                      <TableHead className="font-bold text-slate-600">
                        NIP
                      </TableHead>
                      <TableHead className="font-bold text-slate-600">
                        Nama Dosen
                      </TableHead>
                      {columns.map((col, idx) => (
                        <TableHead
                          key={idx}
                          className="text-right whitespace-nowrap font-bold text-slate-600"
                        >
                          {col}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="text-center py-12 text-slate-500"
                        >
                          {isLoading
                            ? "Memuat..."
                            : "Belum ada data besaran serdos."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      listData.map((row: any, idx: number) => (
                        <TableRow
                          key={row.master_periode_besaran_serdos_id || idx}
                          className="hover:bg-slate-50/50"
                        >
                          <TableCell className="text-center font-medium text-slate-500">
                            {(page - 1) * limit + idx + 1}
                          </TableCell>
                          <TableCell className="font-medium text-slate-700">
                            {row.nip}
                          </TableCell>
                          <TableCell className="text-slate-600 max-w-40 truncate">
                            {row.nama || "-"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700">
                            {formatRupiah(row.rupiah_serdos_1)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700">
                            {formatRupiah(row.rupiah_serdos_2)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700">
                            {formatRupiah(row.rupiah_serdos_3)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700">
                            {formatRupiah(row.rupiah_serdos_4)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700">
                            {formatRupiah(row.rupiah_serdos_5)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-slate-700">
                            {formatRupiah(row.rupiah_serdos_6)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-500">
                      Tampilkan
                    </span>
                    <Select
                      value={limit.toString()}
                      onValueChange={(val) => setLimit(Number(val))}
                    >
                      <SelectTrigger className="w-18 bg-white text-sm rounded-lg shadow-sm border-slate-200 focus:ring-primary h-9">
                        <SelectValue placeholder={limit.toString()} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm font-medium text-slate-500">
                      data
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 border-l border-slate-200 pl-4 hidden sm:block">
                    {totalData > 0 ? (
                      <>
                        Menampilkan{" "}
                        <span className="font-semibold text-slate-700">
                          {listData.length}
                        </span>{" "}
                        dari{" "}
                        <span className="font-semibold text-slate-700">
                          {totalData}
                        </span>
                      </>
                    ) : (
                      <>
                        Menampilkan{" "}
                        <span className="font-semibold text-slate-700">
                          {listData.length}
                        </span>{" "}
                        data
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <span className="text-sm text-slate-600 font-medium">
                    {totalPages
                      ? `Halaman ${page} dari ${totalPages}`
                      : `Halaman ${page}`}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg shadow-sm border-slate-200 hover:bg-slate-50 hover:text-primary transition-colors h-9"
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1 || isLoading}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Sebelumnya
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg shadow-sm border-slate-200 hover:bg-slate-50 hover:text-primary transition-colors h-9"
                      onClick={() => setPage(page + 1)}
                      disabled={!hasMore || isLoading}
                    >
                      Selanjutnya
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
